import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box, CircularProgress, Grid, Modal, Typography } from '@mui/material'
import PatientInformation from './PatientInformation'
import SlidesInformation from './SlidesInformation'
import ReportInformation from './ReportInformation'
import { RootState } from '../../../reducers'
import { useStyles } from '../CaseDiagnosisStyles'
import {
  clearPremerisApiCache,
  clearPremerisReportApiCache,
  getCaseDiagnosis,
  getClinicalInfo,
  getCPTCodes,
  getDraftReportData,
  getPatientHistory,
  getReportData,
  getReportHistory,
  getSampleLocationData,
  getSlideDiagnosis,
  getTrfInfo,
  refreshCaseData,
  clearSessionToken,
} from '../../../actions'
import ErrorSnackBar from '../../HOC/ErrorSnackBar'
import SuccessSnackBar from '../../HOC/SuccesSnackBar'

export default function Summary({
  caseView,
  handleViewerOpen,
  handleReportOpen,
  trfBarcode,
  isAmendFlow,
  isReadOnlyMode,
  handleReleaseReport,
  handleConsultCase,
  handleConsultComplete,
}) {
  const dispatch = useDispatch()
  const classes = useStyles()
  const [isLoading, setLoading] = useState(false)
  const [caseRefresh, setCaseRefresh] = useState(false)
  const clinicalInfo = useSelector((state: RootState) => state.addClinicalInfo)
  const trfInfo = useSelector((state: RootState) => state.addTrfInfo)
  const sampleLocationData = useSelector((state: RootState) => state.addSampleLocationData)
  const patientHistory = useSelector((state: RootState) => state.setPatientHistory)
  const cptCodes = useSelector((state: RootState) => state.getCPTCodesData)
  const reportHistory = useSelector((state: RootState) => state.getReportHistoryData)
  const slideDiagnosis = useSelector((state: RootState) => state.getSlideDiagnosisData)
  const draftReportData = useSelector((state: RootState) => state.setDraftReportData)
  const { procedureCodeId } = useSelector((state: RootState) => state.getCaseData)

  useEffect(() => {
    setLoading(true)
    if (trfBarcode) {
      dispatch(clearSessionToken())
      dispatch(getTrfInfo({ trfBarcode: trfBarcode }))
      dispatch(getSampleLocationData({ trfBarcode: trfBarcode, procedureCode: procedureCodeId }))
      dispatch(getClinicalInfo({ trfBarcode: trfBarcode }))
      dispatch(getPatientHistory({ trfBarcode: trfBarcode }))
      dispatch(getCPTCodes({ trfBarcode: trfBarcode }))
      dispatch(getReportHistory({ trfBarcode: trfBarcode }))
      dispatch(getSlideDiagnosis({ trfBarcode: trfBarcode }))
      dispatch(getDraftReportData({ trfBarcode: trfBarcode }))
      dispatch(getCaseDiagnosis({ procedureCode: procedureCodeId }))
      dispatch(getReportData({ trfBarcode: trfBarcode, procedureCode: procedureCodeId }))
      setCaseRefresh(false)
    }
  }, [trfBarcode, caseRefresh])

  useEffect(() => {
    if (
      isLoading &&
      clinicalInfo.payload &&
      cptCodes.payload &&
      draftReportData &&
      reportHistory.payload &&
      sampleLocationData.payload &&
      trfInfo &&
      slideDiagnosis.payload
    ) {
      setTimeout(() => setLoading(false), 1800)
    }
  }, [clinicalInfo, cptCodes, draftReportData, patientHistory, reportHistory, sampleLocationData, trfInfo, isLoading])

  const { refresh_success, refresh_error } = useSelector((state: RootState) => state.refreshResponse)

  const [openSuccessSnackBar, setOpenSuccessSnackBar] = useState(false)

  const handleRefreshCases = async () => {
    if (!isReadOnlyMode) {
      await dispatch(refreshCaseData({ trfBarcode: trfBarcode }))
      setOpenSuccessSnackBar(true)
      await dispatch(clearPremerisApiCache())
      await dispatch(clearPremerisReportApiCache())
      setCaseRefresh(true)
      setTimeout(() => setOpenSuccessSnackBar(false), 4000)
    }
  }

  return (
    <Box sx={{ width: '100%', ml: '40px' }}>
      {refresh_success && openSuccessSnackBar && <SuccessSnackBar message="Case refresh is successful" />}
      {refresh_error && openSuccessSnackBar && <ErrorSnackBar message="Case refresh failed" />}
      {isLoading ? (
        <CircularProgress className={classes.loadingIndicator} />
      ) : (
        <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }} style={{ height: '98%' }}>
          <Grid item xs={2.8}>
            <PatientInformation isReadOnlyMode={isReadOnlyMode} handleRefreshCases={handleRefreshCases} />
          </Grid>
          <Grid item xs={4.2}>
            <SlidesInformation
              isReadOnlyMode={isReadOnlyMode}
              trfBarcode={trfBarcode}
              handleViewerOpen={handleViewerOpen}
              handleReportOpen={handleReportOpen}
            />
          </Grid>
          <Grid item xs={5}>
            <ReportInformation
              caseView={caseView}
              isAmendFlow={isAmendFlow}
              isReadOnlyMode={isReadOnlyMode}
              trfBarcode={trfBarcode}
              handleReleaseReport={handleReleaseReport}
              handleConsultCase={handleConsultCase}
              handleConsultComplete={handleConsultComplete}
            />
          </Grid>
        </Grid>
      )}
    </Box>
  )
}
